import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Home, ArrowLeft, Search, Briefcase, Zap, Terminal, WifiOff, AlertTriangle } from "lucide-react";
import { useLocation } from "wouter";
import tapLogo from "@/assets/tap-logo.png";

export default function NotFound() {
  const [_, setLocation] = useLocation();

  const handleGoHome = () => {
    setLocation("/");
  };

  const handleGoBack = () => {
    window.history.back();
  };

  const handleGoToDashboard = () => {
    setLocation("/dashboard");
  };

  const handleGoToJobs = () => {
    setLocation("/jobs");
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-slate-100 via-orange-100/55 to-blue-100/45 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-white/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-32 right-20 w-40 h-40 bg-blue-200/30 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-orange-200/20 rounded-full blur-2xl"></div>
        
        {/* Subtle network pattern */}
        <div className="absolute top-32 left-20 w-16 h-16 border border-white/30 rounded-full"></div>
        <div className="absolute bottom-40 right-32 w-12 h-12 border border-white/30 rounded-full"></div>
        <div className="absolute top-64 right-20 w-8 h-8 bg-white/40 rounded-full"></div>
        <div className="absolute bottom-64 left-40 w-6 h-6 bg-white/40 rounded-full"></div>
      </div>

      <Card className="w-full max-w-lg mx-4 shadow-2xl bg-white/95 backdrop-blur-sm border-0 relative z-10">
        <CardContent className="pt-8 pb-8 px-8 text-center">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img 
              src={tapLogo} 
              alt="AirTalent T.A.P. Logo" 
              className="w-20 h-20 object-contain"
            />
          </div>

          {/* 404 Text with brand styling */}
          <div className="mb-6">
            <h1 className="text-6xl font-bold text-transparent bg-gradient-to-r from-[#594FE0] via-[#4175E6] to-[#34C7FA] bg-clip-text space-grotesk mb-2">
              404
            </h1>
            <h2 className="text-2xl font-bold text-gray-900 space-grotesk uppercase tracking-wide mb-3 flex items-center justify-center gap-2">
              <WifiOff className="w-6 h-6" />
              Connection Lost
            </h2>
            <p className="text-gray-600 text-base manrope leading-relaxed max-w-md mx-auto mt-3">
              You've entered the digital void, traveler. This URL doesn't exist in our matrix. Time to respawn and try a different route!
            </p>
          </div>

          {/* Action buttons */}
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                variant="primary" 
                size="md"
                onClick={handleGoHome}
                className="flex items-center gap-2 min-w-[160px]"
              >
                <Home className="w-4 h-4" />
                Respawn Home
              </Button>
              
              <Button 
                variant="secondary" 
                size="md"
                onClick={handleGoBack}
                className="flex items-center gap-2 min-w-[160px]"
              >
                <ArrowLeft className="w-4 h-4" />
                Rewind
              </Button>
            </div>
          </div>

          {/* Footer text */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-gray-500 text-sm manrope flex items-center justify-center gap-2">
              Need a help? Hit up our support squad or teleport back to base!
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
